import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Loader, TextField } from "@/shared/ui";
import { IconMicrophone, IconPlayerStopFilled, IconRefresh, IconArrowLeft, IconCircleCheck, } from "@tabler/icons-react";
import { validateClone, verifyClone, isVoiceProfileReady, isVoiceProfileAwaitingVerify, } from "@/shared/api/voice";
import { pollVoiceProfile, PollCancelledError, } from "../lib/pollVoiceProfile";
import { useAudioRecorder } from "../lib/useAudioRecorder";
import { MicLevelMeter } from "./MicLevelMeter";
import styles from "./VoiceCloneRecorder.module.scss";
/** Начальный шаг для resume-профиля по его статусу. */
const resumeInitialStep = (p) => {
    const s = String(p.status).toLowerCase();
    if (s === "ready")
        return "done";
    if (s === "failed")
        return "failed";
    if (s === "awaiting_verify")
        return "verify";
    return "processing"; // validating / processing
};
// Требования к длительности записи голоса: нижняя граница обязательна для
// качества клона, верхняя — ориентир Suno (референс до ~1 минуты).
const MIN_RECORDING_SEC = 15;
const MAX_RECORDING_SEC = 60;
const MAX_RECORDING_MS = MAX_RECORDING_SEC * 1000;
// Таймер — всегда mm:ss с ведущими нулями (00:00, 00:03…).
const fmtTimer = (ms) => {
    const total = Math.floor(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};
// Согласие. Бренд сервиса — «Виртуоз» (уточнить у владельца при необходимости).
const CONSENT_TEXT_RU = "Нажимая «Начать запись», я даю согласие на обработку и использование моего " +
    "голоса для создания песен в сервисе «Виртуоз» и подтверждаю, что мне " +
    "исполнилось 18 лет.";
const CONSENT_TEXT_EN = "By tapping «Начать запись», I consent to the processing and use of my voice " +
    "to create songs in the «Виртуоз» service and confirm that I am at least 18 " +
    "years old.";
// «Как это работает» — три шага на первой странице флоу «Свой голос».
const HOW_IT_WORKS_RU = [
    "Запишите около минуты своего голоса — читайте текст с экрана живо и с " +
        "выражением, можно напевать. Так нейросеть лучше научится петь вашим голосом.",
    "Нейросеть пришлёт контрольную фразу — прочитайте её вслух, чтобы " +
        "подтвердить, что голос ваш.",
    "Через минуту голос будет готов — выбирайте его при создании песни.",
];
const HOW_IT_WORKS_EN = [
    "Record about a minute of your voice — read the text on screen lively and " +
        "with expression, humming is fine. This helps the neural network learn to " +
        "sing in your voice.",
    "The neural network will send a control phrase — read it aloud to confirm " +
        "the voice is yours.",
    "In a minute your voice will be ready — pick it when creating a song.",
];
// Текст для чтения вслух на шаге 1 (свободная запись голоса). Показывается
// на экране, пользователь читает его с выражением. Первый абзац — устное
// согласие (обработка голоса для создания песен в «Виртуоз» + подтверждение
// 18 лет): пользователь проговаривает его прямо в запись. Дальше — стих для
// богатой фонетики. Переносы строк значимы.
const READING_VERSE = `Я подтверждаю, что мне исполнилось восемнадцать лет, и добровольно даю согласие на обработку и использование моего голоса для создания песен в сервисе «Виртуоз».

За окном плывёт вечерний город,
Фонари роняют мягкий свет.
Я иду, приподнимая ворот,
И мурлычу песенку в ответ.

Где-то поезд тянет долгий голос,
Капли барабанят по стеклу.
Каждый звук ложится, словно колос,
В золотую тёплую копну.

Пахнет чаем, хлебом и корицей,
Кошка жмурит жёлтые глаза.
За рекою вспыхнула зарница,
Пролетела мимо стрекоза.

Синий вечер прячется в ладони,
Ветер шепчет что-то у ворот.
Я пою — и в этой доброй песне
Целый мир со мною оживёт.`;
/**
 * Панель записи (переиспользуется на шаге голоса и шаге пения фразы):
 * запись/стоп/перезапись + превью-плеер. Кнопка отправки — снаружи.
 */
const RecorderPanel = ({ language, recorder, minSec }) => {
    const ru = language === "ru";
    const { state, stream, durationMs, previewUrl, start, stop, reset, error } = recorder;
    const durationSec = Math.floor(durationMs / 1000);
    const tooShort = minSec != null && state === "recorded" && durationSec < minSec;
    if (state === "unsupported") {
        return (_jsx(Alert, { tone: "warning", children: ru
                ? "Запись с микрофона не поддерживается в этом браузере. Откройте сайт в актуальном Chrome/Safari на десктопе или телефоне."
                : "Microphone recording is not supported in this browser. Open the site in an up-to-date Chrome/Safari." }));
    }
    return (_jsxs("div", { className: styles.recorderPanel, children: [state === "denied" && (_jsx(Alert, { tone: "error", children: ru
                    ? "Доступ к микрофону отклонён. Разрешите доступ в настройках браузера и попробуйте снова."
                    : "Microphone access was denied. Allow it in the browser settings and try again." })), state === "error" && error && _jsx(Alert, { tone: "error", children: error }), _jsxs("div", { className: styles.recStage, children: [_jsxs("button", { type: "button", className: `${styles.recCircle} ${state === "recording" ? styles.recCircleActive : ""}`, onClick: state === "recording" ? stop : () => void start(), "aria-label": state === "recording"
                            ? ru
                                ? "Остановить запись"
                                : "Stop recording"
                            : ru
                                ? "Начать запись"
                                : "Start recording", children: [state === "recording" && (_jsx("span", { className: styles.recRing, "aria-hidden": "true" })), _jsx("span", { className: styles.recCircleInner, children: state === "recording" ? (_jsxs(_Fragment, { children: [_jsx(IconPlayerStopFilled, { size: 30 }), _jsx("span", { className: styles.recCircleLabel, children: ru ? "стоп" : "stop" })] })) : (_jsxs(_Fragment, { children: [_jsx(IconMicrophone, { size: 30 }), _jsx("span", { className: styles.recCircleLabel, children: state === "recorded"
                                                ? ru
                                                    ? "заново"
                                                    : "again"
                                                : ru
                                                    ? "Запись"
                                                    : "Record" })] })) })] }), _jsx("span", { className: styles.recTimer, "aria-live": "polite", children: fmtTimer(durationMs) })] }), minSec != null && state !== "recording" && !tooShort && (_jsx("p", { className: styles.recCaption, children: ru
                    ? `Запишите от ${minSec} секунд до 1 минуты.`
                    : `Record from ${minSec} seconds up to 1 minute.` })), tooShort && (_jsx(Alert, { tone: "warning", children: ru
                    ? `Слишком коротко: запишите минимум ${minSec} сек (ещё ${minSec - durationSec} сек).`
                    : `Too short: record at least ${minSec} sec (${minSec - durationSec} sec more).` })), state === "recording" && (_jsxs(_Fragment, { children: [_jsx(MicLevelMeter, { stream: stream }), _jsx("p", { className: styles.recCaption, children: ru
                            ? "Идёт запись… нажмите ещё раз, чтобы остановить."
                            : "Recording… tap again to stop." })] })), state === "recorded" && previewUrl && (_jsxs("div", { className: styles.recordedRow, children: [_jsx("audio", { className: styles.preview, src: previewUrl, controls: true }), _jsx(Button, { variant: "ghost", size: "sm", leftSection: _jsx(IconRefresh, { size: 14 }), onClick: reset, children: ru ? "Сбросить" : "Reset" })] }))] }));
};
export const VoiceCloneRecorder = ({ language, onBack, onCreated, resumeProfile, }) => {
    const ru = language === "ru";
    // Resume: стартовый шаг/идентификаторы берём из переданного профиля, чтобы не
    // мигнуть экраном согласия. Обычный флоу «с нуля» — как раньше (consent).
    const [step, setStep] = useState(() => resumeProfile ? resumeInitialStep(resumeProfile) : "consent");
    const [agreed, setAgreed] = useState(false);
    const [name, setName] = useState(() => resumeProfile?.name ?? "");
    const [profileId, setProfileId] = useState(() => resumeProfile?.id ?? null);
    const [phrase, setPhrase] = useState(() => resumeProfile?.phrase ?? null);
    const [busy, setBusy] = useState(false);
    const [busyMsg, setBusyMsg] = useState(null);
    const [error, setError] = useState(null);
    const [readyProfile, setReadyProfile] = useState(() => resumeProfile && String(resumeProfile.status).toLowerCase() === "ready"
        ? resumeProfile
        : null);
    // Оба рекордера с авто-стопом на 60с; нижняя граница (15с) обязательна только
    // для записи голоса — для пения контрольной фразы её не требуем (фраза короче).
    const voiceRecorder = useAudioRecorder({ maxDurationMs: MAX_RECORDING_MS });
    const verifyRecorder = useAudioRecorder({ maxDurationMs: MAX_RECORDING_MS });
    // Голос записан достаточно длинно (≥ 15с) — можно отправлять на проверку.
    const voiceDurationSec = Math.floor(voiceRecorder.durationMs / 1000);
    const canSubmitVoice = voiceRecorder.state === "recorded" &&
        voiceDurationSec >= MIN_RECORDING_SEC;
    // Флаг размонтирования — гасит поллинг и setState после закрытия.
    const cancelledRef = useRef(false);
    useEffect(() => {
        cancelledRef.current = false;
        return () => {
            cancelledRef.current = true;
        };
    }, []);
    // Resume в статусе validating/processing: профиль уже создан на бэке и
    // дообрабатывается — поллим до READY и показываем экран «идёт обработка».
    // Один раз на маунт (resumeProfile стабилен в рамках открытия рекордера).
    const hasStartedResumePoll = useRef(false);
    useEffect(() => {
        if (step !== "processing" || profileId == null)
            return;
        if (hasStartedResumePoll.current)
            return;
        hasStartedResumePoll.current = true;
        (async () => {
            try {
                const ready = await pollVoiceProfile(profileId, {
                    until: (p) => isVoiceProfileReady(p),
                    isCancelled: () => cancelledRef.current,
                });
                if (cancelledRef.current)
                    return;
                setReadyProfile(ready);
                onCreated(ready);
                setStep("done");
            }
            catch (e) {
                if (e instanceof PollCancelledError)
                    return;
                if (cancelledRef.current)
                    return;
                setError(readErrorMessage(e));
                setStep("failed");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, profileId]);
    // Перезапуск флоу «с нуля» (после FAILED): сбрасываем идентификаторы/записи и
    // возвращаемся к экрану согласия — старый профиль не переиспользуем.
    const restartFromScratch = useCallback(() => {
        setError(null);
        setProfileId(null);
        setPhrase(null);
        setReadyProfile(null);
        setAgreed(false);
        voiceRecorder.reset();
        verifyRecorder.reset();
        hasStartedResumePoll.current = true; // не запускать resume-поллинг заново
        setStep("consent");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const readErrorMessage = (e) => {
        const anyErr = e;
        return (anyErr?.response?.data?.detail ||
            (ru ? "Что-то пошло не так. Попробуйте ещё раз." : "Something went wrong. Try again."));
    };
    // Шаг 1 → отправка записи голоса + поллинг до awaiting_verify.
    const handleSubmitVoice = useCallback(async () => {
        const file = voiceRecorder.toFile("voice");
        if (!file)
            return;
        setError(null);
        setBusy(true);
        setBusyMsg(ru ? "Проверяем запись голоса…" : "Checking your voice recording…");
        try {
            // vocal_start_s / vocal_end_s должны укладываться в 0..duration, а сама
            // длительность — в [15, 60]с (нижнюю границу гейтит кнопка, верхнюю —
            // авто-стоп рекордера; здесь дополнительно клампим на всякий случай).
            const durationSec = Math.min(MAX_RECORDING_SEC, Math.max(MIN_RECORDING_SEC, Math.round(voiceRecorder.durationMs / 1000)));
            const created = await validateClone(file, 0, durationSec, name.trim() || undefined, ru ? "ru" : "en");
            setProfileId(created.id);
            const validated = await pollVoiceProfile(created.id, {
                until: (p) => isVoiceProfileAwaitingVerify(p) || !!p.phrase,
                isCancelled: () => cancelledRef.current,
            });
            if (cancelledRef.current)
                return;
            setPhrase(validated.phrase ?? null);
            setStep("verify");
        }
        catch (e) {
            if (e instanceof PollCancelledError)
                return;
            setError(readErrorMessage(e));
        }
        finally {
            if (!cancelledRef.current) {
                setBusy(false);
                setBusyMsg(null);
            }
        }
    }, [voiceRecorder, name, ru]);
    // Шаг 2 → отправка записи пения фразы + поллинг до ready.
    const handleSubmitVerify = useCallback(async () => {
        const file = verifyRecorder.toFile("verify");
        if (!file || profileId == null)
            return;
        setError(null);
        setBusy(true);
        setBusyMsg(ru ? "Создаём ваш голос…" : "Building your voice…");
        try {
            await verifyClone(profileId, file, {
                voiceName: name.trim() || undefined,
            });
            const ready = await pollVoiceProfile(profileId, {
                until: (p) => isVoiceProfileReady(p),
                isCancelled: () => cancelledRef.current,
            });
            if (cancelledRef.current)
                return;
            setReadyProfile(ready);
            onCreated(ready);
            setStep("done");
        }
        catch (e) {
            if (e instanceof PollCancelledError)
                return;
            setError(readErrorMessage(e));
        }
        finally {
            if (!cancelledRef.current) {
                setBusy(false);
                setBusyMsg(null);
            }
        }
    }, [verifyRecorder, profileId, name, ru, onCreated]);
    if (busy) {
        return (_jsxs("div", { className: styles.busyState, children: [_jsx(Loader, { size: "lg" }), _jsx("p", { className: styles.busyMsg, children: busyMsg }), _jsx("p", { className: styles.busySub, children: ru
                        ? "Пожалуйста, не закрывайте страницу — это займёт около минуты."
                        : "Please don't close the page — this takes about a minute." })] }));
    }
    return (_jsxs("div", { className: styles.recorder, children: [step !== "done" && (_jsxs("button", { type: "button", className: styles.backLink, onClick: onBack, children: [_jsx(IconArrowLeft, { size: 14 }), ru ? "К списку голосов" : "Back to voices"] })), error && (_jsx(Alert, { tone: "error", className: styles.errorAlert, children: error })), step === "consent" && (_jsxs("div", { className: styles.section, children: [_jsx("h3", { className: styles.stepTitle, children: ru ? "Запись своего голоса" : "Record your voice" }), _jsxs("div", { className: styles.howBox, children: [_jsx("div", { className: styles.howTitle, children: ru ? "Как это работает" : "How it works" }), _jsx("ol", { className: styles.howList, children: (ru ? HOW_IT_WORKS_RU : HOW_IT_WORKS_EN).map((item, i) => (_jsx("li", { className: styles.howItem, children: item }, i))) })] }), _jsx("p", { className: styles.consentText, children: ru ? CONSENT_TEXT_RU : CONSENT_TEXT_EN }), _jsxs("label", { className: styles.checkboxRow, children: [_jsx("input", { type: "checkbox", checked: agreed, onChange: (e) => setAgreed(e.target.checked) }), _jsx("span", { children: ru
                                    ? "Я подтверждаю согласие и что это мой голос"
                                    : "I confirm my consent and that this is my voice" })] }), _jsxs("div", { className: styles.actions, children: [_jsx(Button, { variant: "ghost", size: "md", onClick: onBack, children: ru ? "Отмена" : "Cancel" }), _jsx(Button, { variant: "primary", size: "md", disabled: !agreed, onClick: () => setStep("voice"), children: ru ? "Начать запись" : "Start recording" })] })] })), step === "voice" && (_jsxs("div", { className: styles.section, children: [_jsx("h3", { className: styles.stepTitle, children: ru ? "Шаг 1 из 2 — запись голоса" : "Step 1 of 2 — record voice" }), _jsx("p", { className: styles.stepHint, children: ru
                            ? "Читайте текст ниже вслух живо и с выражением. Запишите от 15 секунд до 1 минуты. Можно напевать."
                            : "Read the text below aloud, lively and with expression. Record from 15 seconds up to 1 minute. Humming is fine." }), _jsx("div", { className: styles.verseBox, children: READING_VERSE }), _jsx(TextField, { label: ru ? "Название голоса (необязательно)" : "Voice name (optional)", value: name, maxLength: 60, onChange: (e) => setName(e.target.value), placeholder: ru ? "Например, «Мой голос»" : "e.g. My voice" }), _jsx(RecorderPanel, { language: language, recorder: voiceRecorder, minSec: MIN_RECORDING_SEC }), _jsxs("div", { className: styles.actions, children: [_jsx(Button, { variant: "ghost", size: "md", onClick: () => setStep("consent"), children: ru ? "Назад" : "Back" }), _jsx(Button, { variant: "primary", size: "md", disabled: !canSubmitVoice, onClick: () => void handleSubmitVoice(), children: ru ? "Отправить на проверку" : "Submit for validation" })] })] })), step === "verify" && (_jsxs("div", { className: styles.section, children: [_jsx("h3", { className: styles.stepTitle, children: ru ? "Шаг 2 из 2 — спойте фразу" : "Step 2 of 2 — sing the phrase" }), _jsx("p", { className: styles.stepHint, children: ru
                            ? "Спойте показанную фразу и запишите её — так мы подтвердим ваш голос."
                            : "Sing the phrase below and record it — this verifies your voice." }), _jsx("div", { className: styles.phraseBox, children: phrase && phrase.trim().length > 0
                            ? phrase
                            : ru
                                ? "Фраза не пришла от сервиса — спойте пример из шага 1."
                                : "No phrase returned — sing the example from step 1." }), _jsx(RecorderPanel, { language: language, recorder: verifyRecorder }), _jsx("div", { className: styles.actions, children: _jsx(Button, { variant: "primary", size: "md", disabled: verifyRecorder.state !== "recorded", onClick: () => void handleSubmitVerify(), children: ru ? "Создать голос" : "Create voice" }) })] })), step === "processing" && (_jsxs("div", { className: styles.busyState, children: [_jsx(Loader, { size: "lg" }), _jsx("p", { className: styles.busyMsg, children: ru ? "Идёт обработка голоса…" : "Processing your voice…" }), _jsx("p", { className: styles.busySub, children: ru
                            ? "Это займёт около минуты. Можно закрыть окно — голос появится в списке, когда будет готов."
                            : "This takes about a minute. You can close the window — the voice will appear in the list when it's ready." })] })), step === "failed" && (_jsxs("div", { className: styles.section, children: [_jsx("h3", { className: styles.stepTitle, children: ru ? "Что-то пошло не так" : "Something went wrong" }), _jsx("p", { className: styles.stepHint, children: ru
                            ? "Не удалось завершить создание голоса. Попробуйте записать заново."
                            : "We couldn't finish building the voice. Please try recording again." }), _jsxs("div", { className: styles.actions, children: [_jsx(Button, { variant: "ghost", size: "md", onClick: onBack, children: ru ? "К списку голосов" : "Back to voices" }), _jsx(Button, { variant: "primary", size: "md", onClick: restartFromScratch, children: ru ? "Записать заново" : "Record again" })] })] })), step === "done" && (_jsxs("div", { className: styles.doneState, children: [_jsx(IconCircleCheck, { size: 48, className: styles.doneIcon }), _jsx("h3", { className: styles.stepTitle, children: ru ? "Голос готов!" : "Voice is ready!" }), _jsx("p", { className: styles.stepHint, children: ru
                            ? `«${readyProfile?.name || name || "Мой голос"}» теперь доступен при создании песни.`
                            : `"${readyProfile?.name || name || "My voice"}" is now available when creating a song.` }), _jsx("div", { className: styles.actions, children: _jsx(Button, { variant: "primary", size: "md", onClick: onBack, children: ru ? "К списку голосов" : "Back to voices" }) })] }))] }));
};
